const TEXT_FIT_SELECTOR =
  '[data-slate-editor="true"], textarea.editor-rich-text, .editor-rich-text, textarea, .ql-editor';

export const findTextFitTarget = (rootEl) => {
  if (!rootEl || typeof rootEl.querySelector !== "function") return null;
  return rootEl.querySelector(TEXT_FIT_SELECTOR);
};

const parsePx = (value, fallback = 0) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const isBlankText = (text) => !String(text || "").replace(/\uFEFF/g, "").trim();

/** Drop empty blocks after the last real text; keep blank lines between content. */
const trimTrailingEmptyBlocks = (root) => {
  while (root.lastElementChild && isBlankText(root.lastElementChild.innerText || "")) {
    root.removeChild(root.lastElementChild);
  }
};

const applyEditorFontStyles = (fromEl, toEl) => {
  const computed = window.getComputedStyle(fromEl);
  toEl.style.font = computed.font;
  toEl.style.fontFamily = computed.fontFamily;
  toEl.style.fontSize = computed.fontSize;
  toEl.style.fontWeight = computed.fontWeight;
  toEl.style.fontStyle = computed.fontStyle;
  toEl.style.letterSpacing = computed.letterSpacing;
  toEl.style.lineHeight = computed.lineHeight;
  toEl.style.textTransform = computed.textTransform;
  toEl.style.wordSpacing = computed.wordSpacing;
  toEl.style.wordBreak = computed.wordBreak;
  toEl.style.overflowWrap = computed.overflowWrap;
  toEl.style.padding = computed.padding;
  toEl.style.boxSizing = "border-box";
};

/** Extra room so descenders / spellcheck underlines are not clipped. */
const heightSlackPx = (fontSize) => Math.max(4, Math.ceil(fontSize * 0.2));

/**
 * Measure shrink-wrap size from the text itself.
 * - Default: width = longest unwrapped line (fit-to-content).
 * - With atWidth: height for that constrained width (wrapping), width unchanged.
 * - trimTrailingEmpty: ignore empty lines after the last real text (gaps between text stay).
 */
export const measureTextFitSize = (sourceEl, atWidth, options = {}) => {
  if (!sourceEl || typeof document === "undefined") return null;
  const { trimTrailingEmpty = false } = options;

  const computed = window.getComputedStyle(sourceEl);
  const fontSize = parsePx(computed.fontSize, 16);
  const widthSlack = Math.max(4, Math.ceil(fontSize * 0.12));
  const heightSlack = heightSlackPx(fontSize);
  const constrainedWidth =
    Number.isFinite(atWidth) && atWidth > 0 ? Math.max(1, Math.floor(atWidth)) : null;

  const rawText =
    sourceEl.tagName === "TEXTAREA"
      ? sourceEl.value ?? ""
      : (sourceEl.innerText || "").replace(/\uFEFF/g, "");

  const measure = document.createElement("div");
  applyEditorFontStyles(sourceEl, measure);
  measure.style.position = "absolute";
  measure.style.left = "-99999px";
  measure.style.top = "0";
  measure.style.visibility = "hidden";
  measure.style.pointerEvents = "none";
  measure.style.display = "block";
  measure.style.overflow = "visible";
  measure.style.border = "none";
  measure.style.transform = "none";
  measure.style.maxWidth = "none";
  measure.style.minWidth = "0";
  measure.style.minHeight = "0";
  measure.style.height = "auto";

  if (sourceEl.tagName === "TEXTAREA") {
    measure.textContent = trimTrailingEmpty ? rawText.replace(/\s+$/, "") : rawText;
  } else {
    measure.innerHTML = sourceEl.innerHTML;
    measure.querySelectorAll("[data-slate-placeholder]").forEach((node) => node.remove());
    if (trimTrailingEmpty) trimTrailingEmptyBlocks(measure);
  }

  document.body.appendChild(measure);

  if (constrainedWidth) {
    measure.style.whiteSpace = "pre-wrap";
    measure.style.width = `${constrainedWidth}px`;
    measure.querySelectorAll("*").forEach((node) => {
      node.style.whiteSpace = "pre-wrap";
      node.style.width = "auto";
    });
    const wrappedHeight = Math.ceil(measure.scrollHeight);
    document.body.removeChild(measure);
    const height = Math.max(1, wrappedHeight + heightSlack);
    if (!Number.isFinite(height)) return null;
    return { width: constrainedWidth, height };
  }

  measure.style.width = "max-content";
  measure.style.whiteSpace = "pre";
  measure.querySelectorAll("*").forEach((node) => {
    node.style.whiteSpace = "pre";
    node.style.maxWidth = "none";
    node.style.width = "auto";
  });
  const unwrappedWidth = Math.ceil(measure.scrollWidth);

  measure.style.whiteSpace = "pre-wrap";
  measure.style.width = `${Math.max(unwrappedWidth, 1)}px`;
  measure.querySelectorAll("*").forEach((node) => {
    node.style.whiteSpace = "pre-wrap";
    node.style.width = "auto";
  });
  const wrappedHeight = Math.ceil(measure.scrollHeight);

  document.body.removeChild(measure);

  const width = Math.max(1, unwrappedWidth + widthSlack);
  const height = Math.max(1, wrappedHeight + heightSlack);

  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  return { width, height };
};
